import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Eye,
  EyeOff,
  Lock,
  Check,
  X,
  Copy,
  RefreshCw,
  AlertCircle,
  Shield,
  ArrowRight
} from 'lucide-react';
import { auth } from '@/lib/firebase';
import { verifyPasswordResetCode, confirmPasswordReset, AuthError } from 'firebase/auth';
import GlassCard from '@/components/ui/GlassCard';
import AnimatedPage from '@/components/layout/AnimatedPage';

interface PasswordRequirements {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
}

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [oobCode, setOobCode] = useState<string | null>(null);
  const [email, setEmail] = useState<string>('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [requirements, setRequirements] = useState<PasswordRequirements>({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false
  });

  const [passwordStrength, setPasswordStrength] = useState(0);
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [copied, setCopied] = useState(false);


  useEffect(() => {
    const modeParam = searchParams.get('mode');
    const oobCodeParam = searchParams.get('oobCode');

    setOobCode(oobCodeParam);

    if (!modeParam || !oobCodeParam) {
      setError('Brak wymaganych parametrów. Link jest nieprawidłowy.');
      setIsVerifying(false);
      return;
    }

    if (modeParam === 'resetPassword') {
      verifyCode(oobCodeParam);
    } else {
      setError('Nieprawidłowy tryb operacji.');
      setIsVerifying(false);
      return;
    }
  }, [searchParams]);

  const verifyCode = async (code: string) => {
    if (!auth) {
      setError('Firebase Auth nie jest zainicjowany.');
      setIsVerifying(false);
      return;
    }
    try {
      console.log('Verifying password reset code:', code?.substring(0, 10) + '...');
      const userEmail = await verifyPasswordResetCode(auth, code);
      console.log('Code verified successfully for email:', userEmail);
      setEmail(userEmail);
      setIsVerifying(false);
    } catch (err) {
      console.error('Code verification error:', err);
      const authError = err as AuthError;
      handleFirebaseError(authError);
      setIsVerifying(false);
    }
  };



  const resetPasswordViaFirebase = async (code: string, newPassword: string) => {
    if (!auth) {
      throw new Error('Firebase Auth nie jest zainicjowany.');
    }
    try {
      console.log('Attempting password reset with code:', code?.substring(0, 10) + '...');
      await confirmPasswordReset(auth, code, newPassword);
      console.log('Password reset successful');
    } catch (err) {
      console.error('Password reset error:', err);
      throw err;
    }
  };

  const handleFirebaseError = (error: AuthError) => {
    console.error('Firebase Error Details:', {
      code: error.code,
      message: error.message,
      customData: error.customData,
      name: error.name
    });

    switch (error.code) {
      case 'auth/expired-action-code':
        setError('Link resetowania hasła wygasł. Proszę poprosić o nowy link resetowania hasła.');
        break;
      case 'auth/invalid-action-code':
        setError('Link resetowania hasła jest nieprawidłowy lub został już użyty. Proszę poprosić o nowy link.');
        break;
      case 'auth/weak-password':
        setError('Hasło jest zbyt słabe. Proszę użyć silniejszego hasła.');
        break;
      case 'auth/network-request-failed':
        setError('Błąd połączenia sieciowego. Proszę sprawdzić połączenie internetowe i spróbować ponownie.');
        break;
      case 'auth/user-disabled':
        setError('Konto użytkownika zostało zablokowane. Skontaktuj się z obsługą.');
        break;
      case 'auth/user-not-found':
        setError('Konto użytkownika nie zostało znalezione.');
        break;
      case 'auth/invalid-api-key':
        setError('Nieprawidłowy klucz API Firebase. Skontaktuj się z obsługą.');
        break;
      case 'auth/app-deleted':
        setError('Aplikacja Firebase została usunięta. Skontaktuj się z obsługą.');
        break;
      default:
        setError('Wystąpił błąd podczas resetowania hasła: ' + error.message);
    }
  };

  const checkPasswordStrength = (pwd: string) => {
    let strength = 0;
    const newRequirements: PasswordRequirements = {
      minLength: pwd.length >= 12,
      hasUppercase: /[A-Z]/.test(pwd),
      hasLowercase: /[a-z]/.test(pwd),
      hasNumber: /[0-9]/.test(pwd),
      hasSpecial: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(pwd)
    };

    if (newRequirements.minLength) strength += 20;
    if (newRequirements.hasUppercase) strength += 20;
    if (newRequirements.hasLowercase) strength += 20;
    if (newRequirements.hasNumber) strength += 20;
    if (newRequirements.hasSpecial) strength += 20;

    setRequirements(newRequirements);
    setPasswordStrength(strength);
  };

  useEffect(() => {
    checkPasswordStrength(password);
  }, [password]);

  const generateSecurePassword = useCallback(() => {
    const length = Math.floor(Math.random() * 9) + 16;
    const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:,.<>?';
    const array = new Uint32Array(length);
    crypto.getRandomValues(array);

    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset[array[i] % charset.length];
    }

    return password;
  }, []);

  const handleGeneratePassword = () => {
    const newPassword = generateSecurePassword();
    setGeneratedPassword(newPassword);
    setPassword(newPassword);
    setConfirmPassword(newPassword);
  };

  const handleCopyPassword = async () => {
    if (generatedPassword) {
      try {
        await navigator.clipboard.writeText(generatedPassword);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy password:', err);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!oobCode) {
      setError('Brak kodu resetowania hasła.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Hasła nie są identyczne.');
      return;
    }

    if (!Object.values(requirements).every(req => req)) {
      setError('Hasło nie spełnia wszystkich wymagań bezpieczeństwa.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await resetPasswordViaFirebase(oobCode, password);
      setSuccess(true);
    } catch (err) {
      const error = err as AuthError;
      handleFirebaseError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStrengthLabel = () => {
    if (passwordStrength < 40) return 'Słabe';
    if (passwordStrength < 60) return 'Średnie';
    if (passwordStrength < 80) return 'Dobre';
    return 'Silne';
  };

  const floatingShapes = [
    { size: 256, x: '10%', y: '20%', duration: 18, blur: 60, color: 'bg-blue-500/10' },
    { size: 192, x: '70%', y: '60%', duration: 22, blur: 40, color: 'bg-cyan-500/10' },
    { size: 320, x: '40%', y: '80%', duration: 16, blur: 80, color: 'bg-indigo-500/5' },
    { size: 128, x: '80%', y: '10%', duration: 20, blur: 30, color: 'bg-blue-400/10' },
  ];

  if (isVerifying) {
    return (
      <AnimatedPage>
        <div className="relative min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center p-4 overflow-hidden">
          {floatingShapes.map((shape, i) => (
            <motion.div
              key={i}
              className={`absolute rounded-full ${shape.color} blur-3xl`}
              style={{ width: shape.size, height: shape.size, left: shape.x, top: shape.y }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: shape.duration,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}
          <GlassCard className="max-w-md w-full text-center p-8">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="flex justify-center mb-4"
            >
              <RefreshCw className="w-12 h-12 text-blue-400" />
            </motion.div>
            <p className="text-text-primary text-lg">Weryfikacja linku resetowania hasła...</p>
          </GlassCard>
        </div>
      </AnimatedPage>
    );
  }

  if (error && !success) {
    return (
      <AnimatedPage>
        <div className="relative min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center p-4 overflow-hidden">
          {floatingShapes.map((shape, i) => (
            <motion.div
              key={i}
              className={`absolute rounded-full ${shape.color} blur-3xl`}
              style={{ width: shape.size, height: shape.size, left: shape.x, top: shape.y }}
              animate={{
                scale: [1, 1.15, 1],
                opacity: [0.2, 0.5, 0.2],
              }}
              transition={{
                duration: shape.duration,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}
          <GlassCard className="max-w-md w-full text-center p-8">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-text-primary mb-2">Błąd</h2>
            <p className="text-text-muted mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 shadow-lg shadow-blue-500/25"
            >
              Powrót do strony głównej
            </button>
          </GlassCard>
        </div>
      </AnimatedPage>
    );
  }



  if (success) {
    return (
      <AnimatedPage>
        <div className="relative min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center p-4 overflow-hidden">
          {floatingShapes.map((shape, i) => (
            <motion.div
              key={i}
              className={`absolute rounded-full ${shape.color} blur-3xl`}
              style={{ width: shape.size, height: shape.size, left: shape.x, top: shape.y }}
              animate={{
                scale: [1, 1.15, 1],
                opacity: [0.2, 0.5, 0.2],
              }}
              transition={{
                duration: shape.duration,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          ))}
          <GlassCard className="max-w-md w-full text-center p-8">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-8 h-8 text-emerald-400" />
            </div>
            <h2 className="text-2xl font-bold text-text-primary mb-2">Sukces!</h2>
            <p className="text-text-muted mb-6">Twoje hasło zostało pomyślnie zresetowane.</p>
            <button
              onClick={() => navigate('/')}
              className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2"
            >
              Przejdź do logowania
              <ArrowRight className="w-5 h-5" />
            </button>
          </GlassCard>
        </div>
      </AnimatedPage>
    );
  }

  return (
    <AnimatedPage>
      <div className="relative min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 flex items-center justify-center p-4 overflow-hidden">
        {floatingShapes.map((shape, i) => (
          <motion.div
            key={i}
            className={`absolute rounded-full ${shape.color} blur-3xl`}
            style={{ width: shape.size, height: shape.size, left: shape.x, top: shape.y }}
            animate={{
              scale: [1, 1.15, 1],
              opacity: [0.2, 0.5, 0.2],
            }}
            transition={{
              duration: shape.duration,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
        <GlassCard className="max-w-md w-full p-8">
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto">
                <Lock className="w-8 h-8 text-blue-400" />
              </div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Resetowanie hasła
              </h1>
              <p className="text-text-muted">
                Dla: <span className="text-blue-300 font-medium">{email}</span>
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium text-text-secondary">
                  Nowe hasło
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg py-3 px-4 pr-12 text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Wprowadź nowe hasło"
                    required
                    disabled={isSubmitting}
                    autoComplete="new-password"
                    aria-label="Nowe hasło"
                    aria-describedby="password-requirements"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
                    aria-label={showPassword ? 'Ukryj hasło' : 'Pokaż hasło'}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {password && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-text-secondary">Siła hasła:</span>
                    <span className={`text-sm font-medium ${
                      passwordStrength < 40 ? 'text-red-400' :
                      passwordStrength < 60 ? 'text-orange-400' :
                      passwordStrength < 80 ? 'text-yellow-400' :
                      'text-emerald-400'
                    }`}>
                      {getStrengthLabel()}
                    </span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${passwordStrength}%` }}
                      transition={{ duration: 0.3 }}
                      className={`h-full ${
                        passwordStrength < 40 ? 'bg-red-500' :
                        passwordStrength < 60 ? 'bg-orange-500' :
                        passwordStrength < 80 ? 'bg-yellow-500' :
                        'bg-emerald-500'
                      }`}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="confirmPassword" className="text-sm font-medium text-text-secondary">
                  Potwierdź hasło
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full bg-white/5 border border-white/10 rounded-lg py-3 px-4 pr-12 text-text-primary placeholder-text-muted focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    placeholder="Potwierdź nowe hasło"
                    required
                    disabled={isSubmitting}
                    autoComplete="new-password"
                    aria-label="Potwierdź hasło"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
                    aria-label={showConfirmPassword ? 'Ukryj hasło' : 'Pokaż hasło'}
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {confirmPassword && password !== confirmPassword && (
                  <p className="text-red-400 text-sm flex items-center gap-1">
                    <X className="w-4 h-4" /> Hasła nie są identyczne
                  </p>
                )}
                {confirmPassword && password === confirmPassword && (
                  <p className="text-emerald-400 text-sm flex items-center gap-1">
                    <Check className="w-4 h-4" /> Hasła są identyczne
                  </p>
                )}
              </div>

              <div id="password-requirements" className="space-y-2 bg-white/5 rounded-lg p-4 border border-white/5">
                <p className="text-sm font-medium text-text-secondary flex items-center gap-2">
                  <Shield className="w-4 h-4" /> Wymagania bezpieczeństwa:
                </p>
                <ul className="space-y-1 text-sm">
                  <li className={`flex items-center gap-2 ${requirements.minLength ? 'text-emerald-400' : 'text-text-muted'}`}>
                    {requirements.minLength ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    Minimum 12 znaków
                  </li>
                  <li className={`flex items-center gap-2 ${requirements.hasUppercase ? 'text-emerald-400' : 'text-text-muted'}`}>
                    {requirements.hasUppercase ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    Minimum 1 wielka litera
                  </li>
                  <li className={`flex items-center gap-2 ${requirements.hasLowercase ? 'text-emerald-400' : 'text-text-muted'}`}>
                    {requirements.hasLowercase ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    Minimum 1 mała litera
                  </li>
                  <li className={`flex items-center gap-2 ${requirements.hasNumber ? 'text-emerald-400' : 'text-text-muted'}`}>
                    {requirements.hasNumber ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    Minimum 1 cyfra
                  </li>
                  <li className={`flex items-center gap-2 ${requirements.hasSpecial ? 'text-emerald-400' : 'text-text-muted'}`}>
                    {requirements.hasSpecial ? <Check className="w-4 h-4" /> : <X className="w-4 h-4" />}
                    Minimum 1 znak specjalny
                  </li>
                </ul>
              </div>

              <button
                type="button"
                onClick={handleGeneratePassword}
                className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-text-primary font-medium py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-2"
                disabled={isSubmitting}
              >
                <RefreshCw className="w-5 h-5" />
                Generuj silne hasło
              </button>

              {generatedPassword && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="bg-white/5 rounded-lg p-3 space-y-2 border border-white/10"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-text-secondary">Wygenerowane hasło:</span>
                    <button
                      type="button"
                      onClick={handleCopyPassword}
                      className="text-blue-400 hover:text-blue-300 transition-colors flex items-center gap-1 text-sm"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      {copied ? 'Skopiowano!' : 'Kopiuj'}
                    </button>
                  </div>
                  <p className="text-sm text-text-primary font-mono break-all bg-white/10 rounded p-2">
                    {generatedPassword}
                  </p>
                </motion.div>
              )}

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-start gap-2"
                >
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-300">{error}</p>
                </motion.div>
              )}

              <button
                type="submit"
                disabled={isSubmitting || !Object.values(requirements).every(req => req) || password !== confirmPassword}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 disabled:from-blue-800 disabled:to-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 shadow-lg shadow-blue-500/25 flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                      <RefreshCw className="w-5 h-5" />
                    </motion.div>
                    Resetowanie hasła...
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    Zresetuj hasło
                  </>
                )}
              </button>
            </form>

            <div className="text-center">
              <button
                onClick={() => navigate('/')}
                className="text-text-muted hover:text-text-primary transition-colors text-sm"
              >
                Anuluj i wróć do strony głównej
              </button>
            </div>
          </div>
        </GlassCard>
      </div>
    </AnimatedPage>
  );
};

export default ResetPassword;
